import React from 'react';

import {
  TypographyVariant,
  TypographySize,
  TypographyWeight,
  StyledTypography,
  StyledH1,
  StyledH2,
  StyledH3,
  StyledH4,
  StyledH5,
  StyledH6,
  StyledP,
  StyledLabel,
  StyledDiv,
  StyledSpan,
} from './styles';

/**
 * Typography Component
 *
 * Renders semantic HTML elements (h1-h6, p, label, span) with consistent
 * typography styling based on the design system tokens.
 *
 * @example
 * // Using variant (semantic element mapping)
 * <Typography variant="h1">Main Heading</Typography>
 *
 * // Render as p tag with h2 styling
 * <Typography as="p" variant="h2">Paragraph with heading style</Typography>
 *
 * // Custom color and size
 * <Typography variant="body" color="#0B29FB" size="lg">
 *   Custom styled text
 * </Typography>
 */
export type TypographyProps = React.HTMLAttributes<HTMLElement> & {
  /**
   * Semantic typography variant - defines default styling
   * Maps to predefined sizes, weights, and colors
   */
  variant?: TypographyVariant;

  /**
   * HTML element to render as
   * Can override the default element for a variant
   * @default determined by variant (h1 renders as <h1>, body as <p>, label as <label>, etc)
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'div';

  /**
   * Font size override - allows using variant styling with custom size
   * @example <Typography variant="body" size="xl">Larger body text</Typography>
   */
  size?: TypographySize;

  /**
   * Font weight override
   * @example <Typography variant="body" weight="bold">Bold body text</Typography>
   */
  weight?: TypographyWeight;

  /**
   * Text color - accepts any CSS color value
   * Overrides variant default color
   * @example color="#0B29FB" or color="rgb(11, 41, 251)"
   */
  color?: string;

  /**
   * Line height override
   * @example lineHeight={1.8}
   */
  lineHeight?: number | string;

  /**
   * Content to render
   */
  children: React.ReactNode;
};

/**
 * Internal mapping of variant to default element for semantic HTML
 */
const variantElementMap: Record<
  TypographyVariant,
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'label'
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'p',
  bodySmall: 'p',
  label: 'label',
  caption: 'p',
};

/**
 * Map of HTML element to styled component for semantic rendering.
 *
 * Each styled component is bound to its real tag, so the rendered element is
 * deterministic (no reliance on the Emotion `as` prop, which leaks to the DOM
 * with the custom shouldForwardProp config in this Emotion build).
 */
const styledComponentMap = {
  h1: StyledH1,
  h2: StyledH2,
  h3: StyledH3,
  h4: StyledH4,
  h5: StyledH5,
  h6: StyledH6,
  p: StyledP,
  label: StyledLabel,
  div: StyledDiv,
  span: StyledSpan,
};

/**
 * Typography Component
 *
 * Renders semantic text elements with design system consistency
 */
export const Typography = ({
  variant = 'body',
  as,
  size,
  weight,
  color,
  lineHeight,
  children,
  ...rest
}: TypographyProps): React.ReactElement => {
  // Determine which element to render (explicit `as` wins over the variant default)
  const element = (as ||
    variantElementMap[variant]) as keyof typeof styledComponentMap;

  // Get the styled component bound to that real tag (falls back to <span>)
  const StyledComponent = styledComponentMap[element] || StyledTypography;

  return (
    <StyledComponent
      $variant={variant}
      $size={size}
      $weight={weight}
      $color={color}
      $lineHeight={lineHeight}
      {...rest}
    >
      {children}
    </StyledComponent>
  );
};

export default Typography;
